// Shared building blocks for the eRecords pages: page header, panels, list
// rows, search, filter chips, empty states. Visual language after concierge-ui.
import React, { useEffect, useState } from 'react'
import { TypedDocumentNode, useQuery } from 'urql'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Search as SearchIcon, ChevronRight } from '@mui/icons-material'
import AppLink from '../util/AppLink'
import { useURLParams } from '../actions'
import { brandColors, serif } from './brand'
import DateRangePicker from './DateRangePicker'

// GoAlert's urql client suspends by default; these pages render their own loading states.
const noSuspense = { suspense: false }

export function useBrand(): (typeof brandColors)['light'] {
  return brandColors[useTheme().palette.mode]
}

// useNow re-renders every intervalMs so relative times stay current.
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

export function Page(props: {
  title: React.ReactNode
  plainTitle?: boolean // sans-serif, smaller: for long titles like alert summaries
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
  maxWidth?: number
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Box sx={{ maxWidth: props.maxWidth ?? 1200, mx: 'auto', pb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography
            component='h1'
            sx={
              props.plainTitle
                ? {
                    fontSize: 22,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: c.ink,
                  }
                : {
                    fontFamily: serif,
                    fontSize: 30,
                    lineHeight: 1.2,
                    color: c.ink,
                  }
            }
          >
            {props.title}
          </Typography>
          {props.subtitle && (
            <Typography sx={{ fontSize: 14, color: c.muted, mt: 0.5 }}>
              {props.subtitle}
            </Typography>
          )}
        </Box>
        {props.actions && (
          <Stack direction='row' spacing={1} alignItems='center'>
            {props.actions}
          </Stack>
        )}
      </Box>
      {props.children}
    </Box>
  )
}

export function Panel(props: {
  title?: string
  subtitle?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  flush?: boolean // no inner padding (for lists)
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Card sx={{ overflow: 'hidden' }}>
      {(props.title || props.action) && (
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            borderBottom: `1px solid ${c.line}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {props.title && (
              <Typography
                component='h2'
                sx={{ fontFamily: serif, fontSize: 19, color: c.ink }}
              >
                {props.title}
              </Typography>
            )}
            {props.subtitle && (
              <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.25 }}>
                {props.subtitle}
              </Typography>
            )}
          </Box>
          {props.action}
        </Box>
      )}
      <Box sx={props.flush ? undefined : { px: 2.5, py: 2 }}>
        {props.children}
      </Box>
    </Card>
  )
}

export function SearchBox(props: {
  value: string
  onChange: (v: string) => void
  placeholder: string
}): React.JSX.Element {
  // keep typing snappy; push to the URL/query after a short pause
  const [v, setV] = useState(props.value)
  useEffect(() => setV(props.value), [props.value])
  useEffect(() => {
    if (v === props.value) return
    const t = setTimeout(() => props.onChange(v), 250)
    return () => clearTimeout(t)
  }, [v])
  return (
    <TextField
      size='small'
      value={v}
      onChange={(e) => setV(e.target.value)}
      placeholder={props.placeholder}
      sx={{ minWidth: 240, flex: 1, maxWidth: 420 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon fontSize='small' />
          </InputAdornment>
        ),
      }}
    />
  )
}

export type ChipOption<T extends string> = {
  value: T
  label: string
  count?: number
}

export function ChipTabs<T extends string>(props: {
  options: ChipOption<T>[]
  value: T
  onChange: (v: T) => void
}): React.JSX.Element {
  return (
    <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {props.options.map((o) => {
        const active = o.value === props.value
        return (
          <Chip
            key={o.value}
            label={o.count === undefined ? o.label : `${o.label} · ${o.count}`}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => props.onChange(o.value)}
            sx={{ ml: '0 !important' }}
          />
        )
      })}
    </Stack>
  )
}

export function Toolbar(props: {
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        mb: 2,
      }}
    >
      {props.children}
    </Box>
  )
}

// Row is one list entry: leading visual (outside the link, so checkboxes
// work), then a link with title, subtitle, meta and a chevron.
export function Row(props: {
  to?: string
  leading?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  meta?: React.ReactNode
  trailing?: React.ReactNode
  accent?: string // left accent bar color
  chevron?: boolean // default true for linked rows; off when a button follows
}): React.JSX.Element {
  const c = useBrand()
  const content = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        pr: 2.5,
        color: c.ink,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography
            component='div'
            sx={{ fontSize: 13, color: c.muted }}
            noWrap
          >
            {props.subtitle}
          </Typography>
        )}
      </Box>
      {props.meta && (
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          {props.meta}
        </Box>
      )}
      {props.to && props.chevron !== false && (
        <ChevronRight sx={{ color: c.muted, flexShrink: 0 }} />
      )}
    </Box>
  )
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        pl: 2.5,
        position: 'relative',
        '& + &': { borderTop: `1px solid ${c.line}` },
        '&:hover': props.to ? { bgcolor: c.hover } : undefined,
      }}
    >
      {props.accent && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 8,
            bottom: 8,
            width: 4,
            borderRadius: '0 4px 4px 0',
            bgcolor: props.accent,
          }}
        />
      )}
      {props.leading}
      {props.to ? (
        <AppLink
          to={props.to}
          underline='none'
          sx={{ flex: 1, minWidth: 0, display: 'flex' }}
        >
          {content}
        </AppLink>
      ) : (
        content
      )}
      {props.trailing && <Box sx={{ pr: 2.5 }}>{props.trailing}</Box>}
    </Box>
  )
}

export function Empty(props: {
  title: string
  text?: React.ReactNode
  action?: React.ReactNode
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <Typography sx={{ fontFamily: serif, fontSize: 20, color: c.ink }}>
        {props.title}
      </Typography>
      {props.text && (
        <Typography sx={{ fontSize: 14, color: c.muted, mt: 0.5 }}>
          {props.text}
        </Typography>
      )}
      {props.action && <Box sx={{ mt: 2 }}>{props.action}</Box>}
    </Box>
  )
}

export function LoadMore(props: {
  show: boolean
  loading?: boolean
  onClick: () => void
}): React.JSX.Element | null {
  if (!props.show) return null
  return (
    <Box sx={{ textAlign: 'center', py: 1.5 }}>
      <Button onClick={props.onClick} disabled={props.loading}>
        {props.loading ? 'Loading…' : 'Show more'}
      </Button>
    </Box>
  )
}

// IconTile is the rounded square used as a row's leading visual.
export function IconTile(props: {
  children: React.ReactNode
  tone?: 'brand' | 'muted' | 'bad' | 'warn' | 'ok'
}): React.JSX.Element {
  const theme = useTheme()
  const c = useBrand()
  const color = {
    brand: c.ink,
    muted: c.muted,
    bad: theme.palette.error.main,
    warn: c.warn,
    ok: c.ok,
  }[props.tone ?? 'brand']
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color,
        bgcolor:
          props.tone === 'bad'
            ? c.accentSoft
            : props.tone === 'muted'
              ? c.bg
              : c.soft,
        border: `1px solid ${c.line}`,
      }}
    >
      {props.children}
    </Box>
  )
}

function PagedChunk<T>(props: {
  query: TypedDocumentNode | string
  field: string
  input: Record<string, unknown>
  variables?: Record<string, unknown>
  render: (nodes: T[]) => React.ReactNode
  empty: React.ReactNode
  pageSize?: number
  cursor: string
  last: boolean
  onMore: (cursor: string) => void
}): React.JSX.Element {
  const c = useBrand()
  const [q] = useQuery({
    context: noSuspense,
    query: props.query,
    variables: {
      ...props.variables,
      input: {
        ...props.input,
        first: props.pageSize ?? 50,
        after: props.cursor,
      },
    },
    requestPolicy: 'cache-and-network',
  })
  const conn = q.data?.[props.field]
  if (q.error) return <MUIAlert severity='error'>{q.error.message}</MUIAlert>
  if (!conn) {
    return q.fetching ? (
      <Typography sx={{ p: 2.5, color: c.muted }}>Loading…</Typography>
    ) : (
      <React.Fragment />
    )
  }
  if (!conn.nodes.length && !props.cursor)
    return <React.Fragment>{props.empty}</React.Fragment>
  return (
    <React.Fragment>
      {props.render(conn.nodes as T[])}
      <LoadMore
        show={props.last && conn.pageInfo?.hasNextPage}
        loading={q.fetching}
        onClick={() => props.onMore(conn.pageInfo.endCursor)}
      />
    </React.Fragment>
  )
}

// PagedList runs a GraphQL connection query (`field` is the connection's
// name, e.g. "services") page by page with a "Show more" button.
export function PagedList<T>(props: {
  query: TypedDocumentNode | string
  field: string
  input: Record<string, unknown>
  variables?: Record<string, unknown>
  render: (nodes: T[]) => React.ReactNode
  empty: React.ReactNode
  pageSize?: number
}): React.JSX.Element {
  const [cursors, setCursors] = useState<string[]>([''])
  const key = JSON.stringify([props.input, props.variables])
  useEffect(() => setCursors(['']), [key])
  return (
    <React.Fragment>
      {cursors.map((cursor, i) => (
        <PagedChunk<T>
          key={key + cursor}
          {...props}
          cursor={cursor}
          last={i === cursors.length - 1}
          onMore={(next) => setCursors([...cursors, next])}
        />
      ))}
    </React.Fragment>
  )
}

// ---------------------------------------------------------------- time filter

export type TimeRange = { notCreatedBefore?: string; createdBefore?: string }

// useTimeFilter keeps a period in the URL (?period=24h|7d|30d|custom&from=&to=)
// and turns it into AlertSearchOptions fields.
export function useTimeFilter(): {
  period: string
  setPeriod: (v: string) => void
  from: string
  to: string
  setRange: (from: string, to: string) => void
  range: TimeRange
} {
  // one setter for all three: separate useURLParam setters called back to
  // back would each write from the same stale query string
  const [p, setP] = useURLParams({ period: '', from: '', to: '' })
  const { period, from, to } = p
  const setPeriod = (v: string): void => setP({ period: v })
  const setRange = (f: string, t: string): void => setP({ from: f, to: t })
  const hours: Record<string, number> = {
    '24h': 24,
    '7d': 24 * 7,
    '30d': 24 * 30,
  }
  const [now] = useState(() => Date.now())
  let range: TimeRange = {}
  if (hours[period]) {
    range = {
      notCreatedBefore: new Date(now - hours[period] * 3600_000).toISOString(),
    }
  } else if (period === 'custom') {
    range = {
      ...(from
        ? { notCreatedBefore: new Date(from + 'T00:00:00').toISOString() }
        : {}),
      ...(to
        ? {
            createdBefore: new Date(
              new Date(to + 'T00:00:00').getTime() + 86400_000,
            ).toISOString(),
          }
        : {}),
    }
  }
  return { period, setPeriod, from, to, setRange, range }
}

export function TimeFilter(props: {
  f: ReturnType<typeof useTimeFilter>
}): React.JSX.Element {
  const f = props.f
  return (
    <Box
      sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
    >
      <TextField
        select
        size='small'
        label='When'
        value={f.period}
        onChange={(e) => f.setPeriod(e.target.value)}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value=''>Any time</MenuItem>
        <MenuItem value='24h'>Last 24 hours</MenuItem>
        <MenuItem value='7d'>Last 7 days</MenuItem>
        <MenuItem value='30d'>Last 30 days</MenuItem>
        <MenuItem value='custom'>Pick dates…</MenuItem>
      </TextField>
      {f.period === 'custom' && (
        <DateRangePicker
          from={f.from}
          to={f.to}
          openOnMount
          onChange={(from, to) => f.setRange(from, to)}
        />
      )}
    </Box>
  )
}
